'use client';

import React, { useState } from 'react';
import { CheckCircle2, AlertTriangle, Copy, ChevronDown, ChevronUp, XCircle } from 'lucide-react';
import { ImportRow } from './importTypes';
import Icon from '@/components/ui/AppIcon';


interface ImportPreviewTableProps {
  rows: ImportRow[];
}

const STATUS_CONFIG = {
  valid: { label: 'Valid', color: 'text-green-700 bg-green-50 border-green-200', icon: CheckCircle2 },
  duplicate: { label: 'Duplicate', color: 'text-amber-700 bg-amber-50 border-amber-200', icon: Copy },
  error: { label: 'Error', color: 'text-red-700 bg-red-50 border-red-200', icon: XCircle },
};

export default function ImportPreviewTable({ rows }: ImportPreviewTableProps) {
  const [filter, setFilter] = useState<'all' | 'valid' | 'duplicate' | 'error'>('all');
  const [expandedRow, setExpandedRow] = useState<number | null>(null);

  const counts = {
    all: rows.length,
    valid: rows.filter((r) => r.status === 'valid').length,
    duplicate: rows.filter((r) => r.status === 'duplicate').length,
    error: rows.filter((r) => r.status === 'error').length,
  };

  const filtered = filter === 'all' ? rows : rows.filter((r) => r.status === filter);

  return (
    <div className="space-y-4">
      {/* Summary badges */}
      <div className="flex items-center gap-2 flex-wrap">
        {(['all', 'valid', 'duplicate', 'error'] as const).map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all
              ${filter === f
                ? f === 'all' ? 'bg-foreground text-background border-foreground'
                  : f === 'valid' ? 'bg-green-600 text-white border-green-600'
                  : f === 'duplicate'? 'bg-amber-500 text-white border-amber-500' :'bg-red-500 text-white border-red-500' :'bg-white text-muted-foreground border-border hover:border-foreground/30'
              }`}
          >
            {f === 'valid' && <CheckCircle2 size={12} />}
            {f === 'duplicate' && <Copy size={12} />}
            {f === 'error' && <XCircle size={12} />}
            {f === 'all' ? 'All' : f.charAt(0).toUpperCase() + f.slice(1)}
            <span className="ml-0.5 font-mono">{counts[f]}</span>
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="border border-border rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-muted/60 border-b border-border">
                <th className="text-left px-3 py-2.5 font-semibold text-muted-foreground w-8">#</th>
                <th className="text-left px-3 py-2.5 font-semibold text-muted-foreground">Name</th>
                <th className="text-left px-3 py-2.5 font-semibold text-muted-foreground">Email</th>
                <th className="text-left px-3 py-2.5 font-semibold text-muted-foreground">Company</th>
                <th className="text-left px-3 py-2.5 font-semibold text-muted-foreground">Title</th>
                <th className="text-left px-3 py-2.5 font-semibold text-muted-foreground">LinkedIn</th>
                <th className="text-left px-3 py-2.5 font-semibold text-muted-foreground">Status</th>
                <th className="w-8 px-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={8} className="text-center py-8 text-muted-foreground">
                    No rows match this filter.
                  </td>
                </tr>
              )}
              {filtered.map((row) => {
                const cfg = STATUS_CONFIG[row.status];
                const Icon = cfg.icon;
                const isExpanded = expandedRow === row.index;
                const name = [row.mapped.firstName, row.mapped.lastName].filter(Boolean).join(' ') || '—';

                return (
                  <React.Fragment key={row.index}>
                    <tr
                      className={`hover:bg-muted/20 transition-colors ${row.status === 'error' ? 'bg-red-50/30' : row.status === 'duplicate' ? 'bg-amber-50/30' : ''}`}
                    >
                      <td className="px-3 py-2.5 text-muted-foreground font-mono">{row.index + 1}</td>
                      <td className="px-3 py-2.5 font-medium text-foreground">{name}</td>
                      <td className="px-3 py-2.5 text-muted-foreground truncate max-w-[160px]">
                        {row.mapped.email || <span className="italic text-muted-foreground/40">—</span>}
                      </td>
                      <td className="px-3 py-2.5 text-muted-foreground truncate max-w-[120px]">
                        {row.mapped.company || '—'}
                      </td>
                      <td className="px-3 py-2.5 text-muted-foreground truncate max-w-[120px]">
                        {row.mapped.title || '—'}
                      </td>
                      <td className="px-3 py-2.5 text-muted-foreground truncate max-w-[140px]">
                        {row.mapped.linkedinUrl ? (
                          <span className="text-blue-600 truncate">{row.mapped.linkedinUrl}</span>
                        ) : (
                          <span className="italic text-muted-foreground/40">—</span>
                        )}
                      </td>
                      <td className="px-3 py-2.5">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md border text-[10px] font-semibold ${cfg.color}`}>
                          <Icon size={10} />
                          {cfg.label}
                        </span>
                      </td>
                      <td className="px-2 py-2.5">
                        {(row.status === 'duplicate' || row.status === 'error') && (
                          <button
                            onClick={() => setExpandedRow(isExpanded ? null : row.index)}
                            className="text-muted-foreground hover:text-foreground transition-colors"
                          >
                            {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                          </button>
                        )}
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr className={row.status === 'error' ? 'bg-red-50/50' : 'bg-amber-50/50'}>
                        <td colSpan={8} className="px-4 py-2.5">
                          {row.status === 'duplicate' && (
                            <p className="text-xs text-amber-700 flex items-center gap-1.5">
                              <AlertTriangle size={12} />
                              {row.duplicateReason}
                            </p>
                          )}
                          {row.status === 'error' && row.errors?.map((err, i) => (
                            <p key={i} className="text-xs text-red-700 flex items-center gap-1.5">
                              <XCircle size={12} />
                              {err}
                            </p>
                          ))}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {filtered.length > 0 && (
        <p className="text-xs text-muted-foreground text-right">
          Showing {filtered.length} of {rows.length} rows
        </p>
      )}
    </div>
  );
}
